<script setup>
// Auto-extracted chrome for WwwSolar.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "WWW Solar" },
    brandTagline: { type: String, default: "Photovoltaik. Täglich neu." },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "WWW Solar");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "Photovoltaik. Täglich neu.");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,700;0,900;1,500&family=Inter:wght@400;500;600;700&display=swap";
</script>

<template>
    <div class="wws" :style="`--wws-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="wws-mast">
                    <div class="wws-mast__top">
                        <span class="wws-mast__date">{{ today }}</span>
                        <a href="#wws-subscribe" class="wws-mast__cta">Newsletter abonnieren</a>
                    </div>
                    <div class="wws-mast__title">
                        <h1>{{ brandName }}</h1>
                        <p>{{ brandTagline }}</p>
                    </div>
                    <nav class="wws-mast__nav" aria-label="Hauptnavigation">
                        <a href="#wws-today">Heute aktuell</a>
                        <a href="#wws-markt">Markt</a>
                        <a href="#wws-förderung">Förderung</a>
                        <a href="#wws-forschung">Forschung</a>
                        <a href="#wws-check">PV-Check</a>
                        <a href="/ratgeber">Ratgeber</a>
                    </nav>
                </header>
        <slot />
        <footer class="wws-foot">
                    <div class="wws-foot__inner">
                        <p>{{ brandName }} &middot; {{ brandTagline }}</p>
                        <nav><a href="/impressum">Impressum</a><a href="/datenschutz">Datenschutz</a><a href="/agb">AGB</a><a href="/widerruf">Widerruf</a></nav>
                        <p class="wws-foot__copy">&copy; {{ new Date().getFullYear() }} www-solar.de</p>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

.wws { --wws-paper: #ffffff; --wws-ink: #0c0a09; --wws-sub: #57534e; --wws-line: #e7e5e4; --wws-soft: #fafaf9;
    font-family: 'Inter', system-ui, sans-serif; color: var(--wws-ink); background: var(--wws-paper); min-height: 100dvh; line-height: 1.6; -webkit-font-smoothing: antialiased; }
.wws *, .wws *::before, .wws *::after { box-sizing: border-box; }
.wws h1, .wws h2, .wws h3, .wws h4 { font-family: 'Playfair Display', Georgia, serif; font-weight: 700; letter-spacing: -0.015em; line-height: 1.1; margin: 0; }
.wws h2 em, .wws h3 em { font-style: italic; color: var(--wws-accent); }

.wws-mast { border-bottom: 4px double var(--wws-ink); padding: 20px 24px 16px; max-width: 1240px; margin: 0 auto; }
.wws-mast__top { display: flex; justify-content: space-between; align-items: center; font-size: 12px; letter-spacing: 0.06em; color: var(--wws-sub); }
.wws-mast__date { text-transform: uppercase; }
.wws-mast__cta { color: var(--wws-accent); text-decoration: none; font-weight: 600; }
.wws-mast__cta:hover { text-decoration: underline; }
.wws-mast__title { text-align: center; padding: 18px 0 12px; border-top: 1px solid var(--wws-line); margin-top: 14px; }
.wws-mast__title h1 { font-size: clamp(48px, 8vw, 88px); letter-spacing: -0.02em; }
.wws-mast__title p { color: var(--wws-sub); font-style: italic; font-family: 'Playfair Display', serif; font-size: 16px; margin-top: 4px; }
.wws-mast__nav { display: flex; justify-content: center; gap: 28px; padding-top: 16px; border-top: 1px solid var(--wws-line); flex-wrap: wrap; }
.wws-mast__nav a { color: var(--wws-ink); text-decoration: none; font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; }
.wws-mast__nav a:hover { color: var(--wws-accent); }

.wws-notice { max-width: 1240px; margin: 24px auto; padding: 14px 20px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 4px; font-size: 14px; color: #78350f; }
.wws-notice span { font-weight: 700; text-transform: uppercase; font-size: 11px; letter-spacing: 0.12em; margin-right: 10px; padding: 2px 8px; background: #fde68a; border-radius: 3px; }

.wws-today { padding: 32px 24px; max-width: 1240px; margin: 0 auto; }
.wws-today__inner { display: grid; grid-template-columns: 1fr; gap: 32px; }
@media (min-width: 880px) { .wws-today__inner { grid-template-columns: 2fr 1fr; gap: 48px; } }
.wws-today__eyebrow { color: var(--wws-accent); text-transform: uppercase; font-size: 12px; font-weight: 700; letter-spacing: 0.12em; margin-bottom: 14px; }
.wws-today__feature h2 { font-size: clamp(36px, 5vw, 60px); margin-bottom: 16px; text-wrap: balance; }
.wws-today__teaser { font-size: 19px; line-height: 1.55; color: #44403c; max-width: 64ch; }
.wws-today__source { font-size: 13px; color: var(--wws-sub); margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--wws-line); }
.wws-today__source a { color: var(--wws-accent); text-decoration: none; }
.wws-today__source em { font-style: italic; color: #78350f; background: #fef3c7; padding: 1px 6px; border-radius: 3px; }

.wws-checkCard { background: var(--wws-ink); color: #f5f5f4; padding: 32px; border-radius: 6px; }
.wws-checkCard__eye { color: var(--wws-accent); font-size: 11px; text-transform: uppercase; letter-spacing: 0.16em; font-weight: 700; margin-bottom: 12px; }
.wws-checkCard h3 { color: #fff; font-size: 28px; margin-bottom: 8px; }
.wws-checkCard p { color: #d6d3d1; font-size: 14px; margin-bottom: 20px; }
.wws-checkCard__cta { display: inline-block; background: var(--wws-accent); color: #fff; padding: 12px 22px; border-radius: 4px; text-decoration: none; font-weight: 600; font-size: 14px; transition: background 200ms ease; }
.wws-checkCard__cta:hover { background: #0284c7; }

.wws-feed { padding: 48px 24px; max-width: 1240px; margin: 0 auto; }
.wws-feed__inner { display: grid; grid-template-columns: 1fr; gap: 48px; }
@media (min-width: 880px) { .wws-feed__inner { grid-template-columns: 2fr 1fr; gap: 56px; } }
.wws-feed__title { display: flex; align-items: center; gap: 16px; margin-bottom: 28px; font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; }
.wws-feed__rule { flex: 1; height: 1px; background: var(--wws-line); }
.wws-feed__list { list-style: none; padding: 0; margin: 0; display: grid; gap: 0; }
.wws-item { display: grid; grid-template-columns: auto 1fr; gap: 24px; padding: 28px 0; border-bottom: 1px solid var(--wws-line); }
.wws-item:last-child { border-bottom: 0; }
.wws-item__no { font-family: 'Playfair Display', serif; font-size: 42px; color: var(--wws-accent); line-height: 1; opacity: 0.6; }
.wws-item__cat { font-size: 12px; color: var(--wws-sub); text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 8px; font-weight: 600; }
.wws-item__cat em { font-style: italic; color: #78350f; background: #fef3c7; padding: 1px 6px; border-radius: 3px; font-weight: 500; letter-spacing: 0.05em; }
.wws-item h3 { font-size: 24px; margin-bottom: 10px; }
.wws-item__teaser { color: #44403c; font-size: 16px; max-width: 64ch; }
.wws-item__source { font-size: 13px; color: var(--wws-sub); margin-top: 10px; }
.wws-item__source a { color: var(--wws-accent); text-decoration: none; }

.wws-stats { background: var(--wws-soft); border: 1px solid var(--wws-line); padding: 24px; border-radius: 6px; margin-bottom: 24px; }
.wws-stats h4 { font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: var(--wws-accent); margin-bottom: 14px; }
.wws-stats dl { display: grid; gap: 10px; margin: 0; }
.wws-stats dl > div { display: flex; justify-content: space-between; align-items: baseline; border-bottom: 1px dotted var(--wws-line); padding-bottom: 8px; font-size: 14px; }
.wws-stats dl > div:last-child { border-bottom: none; }
.wws-stats dt { color: var(--wws-sub); }
.wws-stats dd { margin: 0; font-weight: 700; font-variant-numeric: tabular-nums; }
.wws-stats__note { font-size: 12px; color: var(--wws-sub); margin-top: 14px; font-style: italic; }
.wws-stats--ad { background: #f0f9ff; border-color: #bae6fd; }
.wws-stats--ad p { font-size: 12px; text-transform: uppercase; letter-spacing: 0.1em; color: #0369a1; font-weight: 700; margin-bottom: 12px; }
.wws-statsAd { display: block; text-decoration: none; color: var(--wws-ink); padding: 12px 0; border-top: 1px solid #bae6fd; }
.wws-statsAd__h { display: block; font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 700; }
.wws-statsAd__s { display: block; font-size: 13px; color: var(--wws-sub); margin-top: 4px; }

.wws-sub { padding: 80px 24px; background: var(--wws-ink); color: #f5f5f4; }
.wws-sub__inner { max-width: 1100px; margin: 0 auto; display: grid; grid-template-columns: 1fr; gap: 40px; align-items: center; }
@media (min-width: 880px) { .wws-sub__inner { grid-template-columns: 1fr 1fr; gap: 64px; } }
.wws-sub__eye { color: var(--wws-accent); text-transform: uppercase; font-size: 11px; letter-spacing: 0.18em; font-weight: 700; margin-bottom: 14px; }
.wws-sub h2 { color: #fff; font-size: clamp(32px, 5vw, 56px); margin-bottom: 18px; }
.wws-sub__body { color: #d6d3d1; font-size: 16px; max-width: 48ch; }
.wws-sub__form { background: #fff; padding: 32px; border-radius: 6px; color: var(--wws-ink); }
.wws-sub__lbl { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
.wws-sub__lbl > span { font-size: 13px; font-weight: 600; }
.wws-sub__lbl em { font-weight: 400; color: var(--wws-sub); font-style: normal; }
.wws-sub__lbl input { padding: 14px 16px; font-size: 16px; border: 1px solid var(--wws-line); border-radius: 4px; font-family: inherit; }
.wws-sub__lbl input:focus { outline: none; border-color: var(--wws-accent); }
.wws-sub__consent { display: flex; gap: 12px; padding: 14px; background: var(--wws-soft); border: 1px solid var(--wws-line); border-radius: 4px; font-size: 13px; color: var(--wws-sub); line-height: 1.5; cursor: pointer; margin-bottom: 16px; }
.wws-sub__consent input { margin-top: 2px; flex-shrink: 0; accent-color: var(--wws-accent); }
.wws-sub__cta { width: 100%; background: var(--wws-accent); color: #fff; padding: 16px; border: none; border-radius: 4px; font-size: 16px; font-weight: 700; cursor: pointer; font-family: inherit; transition: background 200ms ease; }
.wws-sub__cta:hover:not(:disabled) { background: #0284c7; }
.wws-sub__cta:disabled { opacity: 0.5; cursor: not-allowed; }
.wws-sub__err { color: #b91c1c; font-size: 13px; margin-top: 10px; text-align: center; }
.wws-sub__done { text-align: center; padding: 32px 0; }
.wws-sub__doneBig { font-family: 'Playfair Display', serif; font-size: 28px; margin-bottom: 12px; font-weight: 700; }

.wws-check { padding: 80px 24px; max-width: 1240px; margin: 0 auto; text-align: center; }
.wws-check__eye { color: var(--wws-accent); text-transform: uppercase; font-size: 11px; letter-spacing: 0.18em; font-weight: 700; margin-bottom: 14px; }
.wws-check h2 { font-size: clamp(36px, 5vw, 56px); margin-bottom: 16px; }
.wws-check__body { color: var(--wws-sub); max-width: 56ch; margin: 0 auto 36px; font-size: 17px; }
.wws-check__cards { display: grid; grid-template-columns: 1fr; gap: 16px; max-width: 800px; margin: 0 auto; }
@media (min-width: 720px) { .wws-check__cards { grid-template-columns: 1fr 1fr; } }
.wws-check__card { display: block; padding: 32px; background: var(--wws-soft); border: 1px solid var(--wws-line); border-radius: 6px; text-decoration: none; color: var(--wws-ink); text-align: left; transition: all 200ms ease; }
.wws-check__card:hover { border-color: var(--wws-accent); transform: translateY(-2px); }
.wws-check__card p { font-size: 11px; text-transform: uppercase; letter-spacing: 0.16em; color: var(--wws-sub); font-weight: 700; margin-bottom: 8px; }
.wws-check__card h3 { font-size: 24px; margin-bottom: 16px; }
.wws-check__card span { color: var(--wws-accent); font-weight: 600; font-size: 14px; }

.wws-meth { padding: 56px 24px; background: var(--wws-soft); border-top: 1px solid var(--wws-line); }
.wws-meth__inner { max-width: 800px; margin: 0 auto; }
.wws-meth p:first-child { color: var(--wws-accent); text-transform: uppercase; font-size: 11px; letter-spacing: 0.16em; font-weight: 700; margin-bottom: 12px; }
.wws-meth h3 { font-size: clamp(28px, 4vw, 36px); margin-bottom: 20px; }
.wws-meth ul { margin: 0 0 24px; padding-left: 20px; color: var(--wws-sub); display: grid; gap: 8px; }
.wws-meth li { font-size: 16px; }
.wws-meth__fine { font-size: 13px; color: var(--wws-sub); font-style: italic; padding-top: 16px; border-top: 1px solid var(--wws-line); }

.wws-foot { background: var(--wws-ink); color: #d6d3d1; padding: 32px 24px; }
.wws-foot__inner { max-width: 1240px; margin: 0 auto; display: flex; justify-content: space-between; align-items: center; gap: 16px; font-size: 13px; flex-wrap: wrap; }
.wws-foot nav { display: flex; gap: 18px; }
.wws-foot a { color: #f5f5f4; text-decoration: none; }
.wws-foot a:hover { color: var(--wws-accent); }
.wws-foot__copy { font-size: 12px; opacity: 0.7; }

@media (prefers-reduced-motion: reduce) {
    .wws *, .wws *::before, .wws *::after { transition: none !important; animation: none !important; transform: none !important; }
}

/* ── Trust strip (images + cites + byline) — wws-* unique treatment ── */
.wws-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.wws-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .wws-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.wws-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 2px dotted #0EA5E9; box-shadow: 0 6px 22px -14px rgba(20,17,13,0.20);
}
.wws-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.wws-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.wws-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.wws-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.wws-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.wws-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #0EA5E955;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.wws-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.wws-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #0EA5E9;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.wws-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.wws-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.wws-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.wws-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.wws-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.wws-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


</style>
